/**
 * InterestListCard — the lead's future-study lists (owner request 2026-08-20).
 * "Not a fit for what we have now, right profile for a standard study type we
 * regularly get": chips of the lists this lead is on, an add form (active
 * catalog entries not already held + an optional why-they-fit note), and a
 * two-tap remove. Deliberately works on CLOSED/archived leads — that is the
 * population these lists exist for.
 */
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { BookmarkPlus, Loader2, Plus, X } from 'lucide-react';
import api from '../../../services/api';
import { ContactInterest, InterestCategory } from '../../types';
import { relTime, useTeam } from '../../lib';
import { Alert } from '../ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface Props {
  contactId: string;
  reloadToken: number;
  /** ProfileTab's frameless grouped list owns the card chrome. */
  frameless?: boolean;
}

export default function InterestListCard({ contactId, reloadToken, frameless = false }: Props) {
  const { byId } = useTeam();
  const [interests, setInterests] = useState<ContactInterest[]>([]);
  const [categories, setCategories] = useState<InterestCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [note, setNote] = useState('');
  const [armedRemove, setArmedRemove] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const [mine, catalog] = await Promise.all([
        api.get(`/api/crm/contacts/${contactId}/interests`),
        api.get('/api/crm/interest-categories?active=true'),
      ]);
      setInterests(mine.data?.interests || []);
      setCategories(catalog.data?.categories || []);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const addable = useMemo(() => {
    const held = new Set(interests.map((i) => i.category_id));
    return categories.filter((c) => !held.has(c.id));
  }, [interests, categories]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!categoryId || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      const body: Record<string, string> = { category_id: categoryId };
      if (note.trim()) body.note = note.trim();
      const res = await api.post(`/api/crm/contacts/${contactId}/interests`, body);
      const interest: ContactInterest | undefined = res.data?.interest;
      if (interest) {
        // The POST returns the bare row; graft the category so the chip can
        // label itself without a refetch.
        const category = res.data?.category
          || categories.find((c) => c.id === interest.category_id) || null;
        setInterests((prev) => (prev.some((i) => i.id === interest.id)
          ? prev
          : [...prev, { ...interest, category }]));
        setCategoryId('');
        setNote('');
      }
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  /** Two-tap remove (the ProfileListCard delete pattern): first tap arms 3s. */
  const remove = async (interest: ContactInterest) => {
    if (armedRemove !== interest.id) {
      setArmedRemove(interest.id);
      window.setTimeout(() => setArmedRemove((a) => (a === interest.id ? null : a)), 3000);
      return;
    }
    setArmedRemove(null);
    setActionError(null);
    setBusyIds((p) => new Set(p).add(interest.id));
    setInterests((p) => p.filter((i) => i.id !== interest.id));
    try {
      await api.delete(`/api/crm/contacts/${contactId}/interests/${interest.id}`);
    } catch (e: any) {
      setInterests((p) => [...p, interest].sort((a, b) => a.created_at.localeCompare(b.created_at)));
      setActionError(errMsg(e));
    } finally {
      setBusyIds((p) => {
        const n = new Set(p);
        n.delete(interest.id);
        return n;
      });
    }
  };

  const inputCls =
    'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 min-h-[44px] sm:min-h-0';

  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800'}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <BookmarkPlus className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Future studies
          {interests.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({interests.length})</span>
          )}
        </h3>
        {addable.length > 0 && (
          <button
            type="button"
            onClick={() => setFormOpen((o) => !o)}
            aria-label={formOpen ? 'Close the add form' : 'Add to a future-study list'}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
          >
            {formOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {formOpen ? 'Close' : 'Add'}
          </button>
        )}
      </div>

      {formOpen && addable.length > 0 && (
        <form onSubmit={add} className="mb-3 space-y-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            aria-label="Future-study list"
            className={inputCls}
          >
            <option value="">Pick a study type…</option>
            {addable.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why they fit (optional — e.g. BMI 41, otherwise healthy)"
            maxLength={500}
            className={inputCls}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!categoryId || saving}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add
            </button>
          </div>
        </form>
      )}

      {actionError && (
        <p role="alert" className="mb-2 text-xs text-red-600 dark:text-red-400">{actionError}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : error ? (
        <Alert tone="error" size="sm" icon={false}>
          {error}{' '}
          <button onClick={() => { setLoading(true); load(); }} className="font-medium underline">Retry</button>
        </Alert>
      ) : interests.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Not on any future-study list. Add them when they fit a study type we regularly get
          — they'll be one search away when that study opens.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {interests.map((interest) => {
            const busy = busyIds.has(interest.id);
            const adder = interest.added_by ? byId.get(interest.added_by)?.name : null;
            const label = interest.category?.label || 'Unknown list';
            return (
              <li key={interest.id} className="flex items-start justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                  {interest.note && (
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{interest.note}</p>
                  )}
                  <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                    Added {relTime(interest.created_at)}
                    {adder ? ` · ${adder}` : ''}
                    {interest.source === 'disposition' ? ' · at close-out' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(interest)}
                  disabled={busy}
                  aria-label={armedRemove === interest.id ? `Confirm remove from ${label}` : `Remove from ${label}`}
                  title={armedRemove === interest.id ? 'Click again to confirm' : 'Remove from this list'}
                  className={`inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg p-1 transition-colors duration-150 sm:min-h-0 sm:min-w-0 ${
                    armedRemove === interest.id
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700'
                  }`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
